"use client";

import React from "react";
import { MapPin, Phone, Mail, CircleAlert, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-10 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";
const textareaClasses = "flex min-h-[120px] w-full rounded-sm border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 resize-y";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="106723" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 border border-primary/20">
                    <Mail className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-foreground">Request Received</h3>
                <p className="text-muted-foreground max-w-md font-sans">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-sm">
                    New Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <FormField control={control} name="full_name" rules={{
      required: "Full Name is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
                        <FormControl>
                            <input type="text" className={inputClasses} placeholder="Alexander Sterling" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] font-sans">Enter your name as it appears on your travel documents.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
                            <FormControl>
                                <input type="email" className={inputClasses} placeholder="sterling@executive.com" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\d\+\-\(\)\s]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Phone Number <span className="text-muted-foreground/50 font-normal">(Optional)</span></FormLabel>
                            <FormControl>
                                <input type="tel" className={inputClasses} placeholder="+1 (415) 555-0199" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="inquiry_type" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Service Type</FormLabel>
                        <FormControl>
                            <select className={inputClasses} {...field}>
                                {["Airport Transfer (SFO/OAK/SJC)", "Hourly Chauffeur Service", "Corporate Account Inquiry", "Special Event / Wedding", "Other Inquiry"].map((option, i) => <option key={option} value={option} data-index={i} className="bg-card">
                                        {option}
                                    </option>)}
                            </select>
                        </FormControl>
                        <FormDescription className="text-[10px] font-sans">Select the nature of your transportation requirements.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 20,
        message: "Details must be at least 20 characters"
      },
      maxLength: {
        value: 2000,
        message: "Details cannot exceed 2000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Journey Details</FormLabel>
                        <FormControl>
                            <textarea className={textareaClasses} placeholder="Please provide date, flight number (if applicable), and specific vehicle preferences..." {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] font-sans">Include pickup location and time for a more accurate quote.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-sm flex items-start gap-3 text-destructive">
                    <CircleAlert className="size-5 shrink-0 mt-0.5" />
                    <p className="text-sm font-sans font-medium">{submitError}</p>
                </div>}

            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-sm shadow-[0_0_15px_rgba(212,175,55,0.2)]">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Transmitting...
                    </> : 'Confirm Inquiry'}
            </Button>
        </Form>;
}
export default function ReachOurCareTeam() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="always-at-your-service" className="dark bg-background py-20 lg:py-32 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/20 blur-[120px]" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
                    
                    {}
                    <div ref={leftRef} className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <div>
                            <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground tracking-tight mb-4">
                                Always At Your Service
                            </h2>
                            <p className="text-lg text-muted-foreground font-sans leading-relaxed">
                                Experience the pinnacle of San Francisco's private transportation. From SFO arrivals to executive city travel, our chauffeurs are at your command 24 hours a day, 7 days a week.
                            </p>
                        </div>

                        {}
                        <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-sm shadow-lg backdrop-blur-sm">
                            <div className="flex items-start gap-4">
                                <CircleAlert className="size-8 text-primary shrink-0" />
                                <div>
                                    <h3 className="text-xs font-mono font-bold text-primary uppercase tracking-widest mb-1">Immediate Booking?</h3>
                                    <p className="text-foreground font-sans font-medium">
                                        If you require a vehicle within the next 60 minutes, please call our dispatch line directly at <strong className="font-bold text-primary">(415) 866-6288</strong> for priority service.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="size-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                                    <MapPin className="size-6 text-primary" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-serif font-semibold text-foreground mb-1">Headquarters</h4>
                                    <p className="text-muted-foreground font-sans">1475 Rollins Rd<br />Burlingame, CA 94010</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="size-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                                    <Phone className="size-6 text-primary" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-serif font-semibold text-foreground mb-1">Direct Line</h4>
                                    <p className="text-primary text-4xl font-sans font-semibold tracking-tighter">(415) 866-6288</p>
                                    <p className="text-xs font-mono text-muted-foreground mt-2 uppercase tracking-wider">Available 24/7/365</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="size-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                                    <Mail className="size-6 text-primary" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-serif font-semibold text-foreground mb-1">Inquiries</h4>
                                    <p className="text-muted-foreground font-sans">dispatch@aapluxurytransfersusa.com</p>
                                </div>
                            </div>
                        </div>

                        {}
                        <div className="w-full h-64 rounded-sm overflow-hidden shadow-2xl border border-primary/10 relative bg-card">
                            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3152.823674483737!2d-122.4035!3d37.7912!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808b8b8b8b8b%3A0x8b8b8b8b8b8b8b8b!2sOne%20Sansome%20St%2C%20San%20Francisco%2C%20CA%2094104!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="GoldSteer Bay Area Service Zone" className="absolute inset-0 grayscale invert opacity-70 hover:opacity-90 transition-all duration-700"></iframe>
                        </div>
                    </div>

                    {}
                    <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <Card className="border-primary/20 shadow-2xl bg-card/60 backdrop-blur-2xl rounded-sm">
                            <CardHeader className="pb-6 border-b border-primary/10">
                                <CardTitle className="text-2xl font-serif">Inquire for Service</CardTitle>
                                <CardDescription className="text-base font-sans">
                                    Provide your journey details below. Our concierge team will confirm your reservation or provide a bespoke quote within minutes.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <FormProvider formKey="contact_form_reach_us" formId="contact_form_reach_us" sectionName="Always At Your Service" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                inquiry_type: "Airport Transfer (SFO/OAK/SJC)",
                message: ""
              }} submitText="Confirm Inquiry" successMessage="Your request has been received. A GoldSteer concierge will contact you shortly to finalize your luxury travel arrangements.">
                                    <ContactFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}